import { openobserveRum } from '@openobserve/browser-rum';
import { openobserveLogs } from '@openobserve/browser-logs';

// OpenObserve RUM + browser-logs bootstrap.
//
// Entirely disabled unless the three required REACT_APP_OO_* vars below are
// present at build time, so local dev and any build without them is a no-op.
// Values come from the OpenObserve UI: Data Sources -> RUM (Getting Started).
//
//   REACT_APP_OO_SITE            OpenObserve host, e.g. 'o2.example.com' (or 'host:5080')
//   REACT_APP_OO_CLIENT_TOKEN    RUM client token (rum...)
//   REACT_APP_OO_APP_ID          RUM application id
//   REACT_APP_OO_ORG             org identifier (default: 'default')
//   REACT_APP_OO_ENV             'dev' | 'qa' | 'prod' (default: 'dev')
//   REACT_APP_OO_SESSION_SAMPLE  0-100, % of sessions recorded (default: 100)
//   REACT_APP_OO_REPLAY_SAMPLE   0-100, % of sessions with session replay (default: 0 = off)
//   REACT_APP_OO_INSECURE_HTTP   'true' only if the o2 host is plain http (default: false)
//   REACT_APP_VERSION            app version tag shown on RUM data (default: 'unknown')

const pct = (value, fallback) => {
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n >= 0 && n <= 100 ? n : fallback;
};

let rumActive = false;

export function initTelemetry() {
    const site = process.env.REACT_APP_OO_SITE;
    const clientToken = process.env.REACT_APP_OO_CLIENT_TOKEN;
    const applicationId = process.env.REACT_APP_OO_APP_ID;

    if (!site || !clientToken || !applicationId) {
        return; // telemetry not configured for this build
    }

    const shared = {
        clientToken,
        site,
        organizationIdentifier: process.env.REACT_APP_OO_ORG || 'default',
        service: 'core-v1-frontend',
        env: process.env.REACT_APP_OO_ENV || 'dev',
        version: process.env.REACT_APP_VERSION || 'unknown',
        apiVersion: 'v1',
        insecureHTTP: process.env.REACT_APP_OO_INSECURE_HTTP === 'true',
    };

    const replaySample = pct(process.env.REACT_APP_OO_REPLAY_SAMPLE, 0);

    try {
        openobserveRum.init({
            ...shared,
            applicationId,
            sessionSampleRate: pct(process.env.REACT_APP_OO_SESSION_SAMPLE, 100),
            sessionReplaySampleRate: replaySample,
            trackResources: true, // XHR/fetch (all axios API calls) + asset timing
            trackLongTasks: true,
            trackUserInteractions: true, // click actions
            defaultPrivacyLevel: 'mask-user-input',
        });

        openobserveLogs.init({
            ...shared,
            forwardErrorsToLogs: true, // console.error + uncaught exceptions + network errors
        });

        if (replaySample > 0) {
            openobserveRum.startSessionReplayRecording();
        }

        rumActive = true;
    } catch (e) {
        // Telemetry must never take the app down with it.
        console.warn('[telemetry] OpenObserve RUM init failed:', e);
    }
}

// Mirrors the logged-in user from the redux auth state onto RUM sessions, so
// sessions/errors in OpenObserve are attributable to an account. Subscribing
// to the store (rather than patching action creators) covers every path auth
// can change through: login, localStorage rehydrate, resetAuth/setAuth, logout.
export function bindTelemetryUser(store) {
    if (!rumActive) {
        return; // telemetry not configured for this build
    }

    let currentId = null;

    const sync = () => {
        const profile = store.getState().auth?.user?.profile;
        const id = profile?.id != null ? String(profile.id) : null;
        if (id === currentId) {
            return;
        }
        currentId = id;
        try {
            if (id) {
                openobserveRum.setUser({
                    id,
                    name: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || undefined,
                    email: profile.email || undefined,
                });
            } else {
                openobserveRum.removeUser();
            }
        } catch (e) {
            console.warn('[telemetry] RUM user sync failed:', e);
        }
    };

    store.subscribe(sync);
    sync(); // auth may already be populated (e.g. hydrated before binding)
}
